import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Clock } from 'lucide-react';
import { clsx } from 'clsx';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import { useDeployments, useEnvironments, useLatestDeployments } from '@/hooks/useDeployments';
import type { Deployment } from '@/types';

export default function Deployments() {
  const [statusFilter, setStatusFilter] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: environments } = useEnvironments();
  const { data: latestDeploys, isLoading: matrixLoading } = useLatestDeployments();
  const { data: deploymentsData, isLoading, error, refetch } = useDeployments({
    status: statusFilter || undefined,
    environment_id: envFilter || undefined,
    page,
    page_size: pageSize,
  });

  const serviceMap = new Map<string, Map<string, Deployment>>();
  const envNames = new Set<string>();
  (latestDeploys ?? []).forEach((d) => {
    const sName = d.service_slug ?? d.service_name ?? 'unknown';
    const eName = d.environment_name ?? 'unknown';
    envNames.add(eName);
    if (!serviceMap.has(sName)) serviceMap.set(sName, new Map());
    serviceMap.get(sName)!.set(eName, d);
  });
  const sortedEnvs = Array.from(envNames).sort();
  const sortedServices = Array.from(serviceMap.keys()).sort();

  const columns: Column<Deployment>[] = [
    {
      key: 'service_name',
      header: 'Service',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{row.service_name ?? row.service_slug ?? '-'}</span>
      ),
    },
    {
      key: 'environment_name',
      header: 'Environment',
      render: (row) => (
        <span className="text-slate-600 dark:text-slate-400">{row.environment_name ?? '-'}</span>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      render: (row) => (
        <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{row.version ?? '-'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'trigger_type',
      header: 'Trigger',
      render: (row) => (
        <span className="capitalize text-slate-600 dark:text-slate-300">{row.trigger_type?.replace('_', ' ') ?? '-'}</span>
      ),
    },
    {
      key: 'triggered_by',
      header: 'By',
      render: (row) => <span className="text-slate-600 dark:text-slate-400">{row.triggered_by ?? '-'}</span>,
    },
    {
      key: 'duration_seconds',
      header: 'Duration',
      render: (row) =>
        row.duration_seconds ? (
          <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            {Math.floor(row.duration_seconds / 60)}m {row.duration_seconds % 60}s
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">-</span>
        ),
    },
    {
      key: 'started_at',
      header: 'When',
      sortable: true,
      render: (row) => (
        <span className="text-slate-500 dark:text-slate-400">
          {formatDistanceToNow(new Date(row.started_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Deployments"
        description="Track deployments across services and environments."
      />

      {/* Environment Status Matrix */}
      {!matrixLoading && sortedServices.length > 0 && (
        <div className="card overflow-hidden">
          <div className="card-header">
            <h2 className="text-card-title text-slate-900 dark:text-slate-100">Environment Status Matrix</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-surface-50 dark:bg-slate-800/60">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400">
                    Service
                  </th>
                  {sortedEnvs.map((env) => (
                    <th
                      key={env}
                      className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400"
                    >
                      {env}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {sortedServices.map((svc) => (
                  <tr key={svc} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="whitespace-nowrap px-4 py-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {svc}
                    </td>
                    {sortedEnvs.map((env) => {
                      const deploy = serviceMap.get(svc)?.get(env);
                      return (
                        <td key={env} className="whitespace-nowrap px-4 py-2 text-center">
                          {deploy ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <StatusBadge status={deploy.status} />
                              <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
                                {deploy.version ?? ''}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300 dark:text-slate-600">--</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="select-field"
        >
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="in_progress">In Progress</option>
          <option value="pending">Pending</option>
          <option value="rolled_back">Rolled Back</option>
        </select>
        <select
          value={envFilter}
          onChange={(e) => { setEnvFilter(e.target.value); setPage(1); }}
          className="select-field"
        >
          <option value="">All Environments</option>
          {(environments ?? []).map((env) => (
            <option key={env.id} value={env.id}>
              {env.name}
            </option>
          ))}
        </select>
      </div>

      {/* Deployment history table */}
      <div className="card overflow-hidden">
        <div className="card-header">
          <h2 className="text-card-title text-slate-900 dark:text-slate-100">Deployment History</h2>
        </div>
        {isLoading ? (
          <div className="py-16"><LoadingSpinner message="Loading deployments..." /></div>
        ) : error ? (
          <div className="p-4"><ErrorBanner message="Failed to load deployments" onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            data={deploymentsData?.items ?? []}
            keyExtractor={(row) => row.id}
            page={page}
            pageSize={pageSize}
            total={deploymentsData?.total}
            onPageChange={setPage}
            emptyTitle="No deployments found"
          />
        )}
      </div>
    </div>
  );
}
